import * as React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Icon } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { getAntiForgeryToken } from "../../utils/Fetch";
import { containsUnavailableFilters } from "../../services/FilterService";
import { InformationDialog } from "./InformationDialog";
import DialogTitleWithClose from "../../components/DialogTitleWithClose";

interface IProps {
	onSelect: (filterId: string) => void,
	onRemove: (removedIds: string[]) => void,
	typeName: string,
	className: string,
}

export default function FilterManagementDialog(props: IProps) {
	const { t } = useTranslation();
	const [open, setOpen] = React.useState(false);
	const [deleteDisabled, setDeleteDisabled] = React.useState(true);
	const [applyDisabled, setApplyDisabled] = React.useState(true);
	const [errorMessage, setErrorMessage] = React.useState("");

	const [deleteWarning, setDeleteWarning] = React.useState<string[]>(null);
	const [informationDialog, setInformationDialog] = React.useState<string>(null);
	const [grid, setGrid] = React.useState<any>(null);

	function handleGridFilterDeleteClick() {

		var aSelectedIds = (window as any).fnGetValuesByColumn("Id", grid);
		containsUnavailableFilters(aSelectedIds, (result) => {
			
			if (result) {
				setInformationDialog(t("control.filtering.selectedFiltersContainsUnavailableFilter"));
			} else {
				setDeleteWarning(aSelectedIds);
			}
		});
	}

	function handleAcceptDelete() {
		setErrorMessage("");
		(window as any).$.ajax({
			url: "/Filters/Remove",
			data: {
				__RequestVerificationToken: getAntiForgeryToken(),
				filterIds: deleteWarning,
			},
			type: 'POST',
			traditional: true,
			success: (data) => {
				if (data.success) {
					grid.fnDraw();
					props.onRemove(deleteWarning);
					setDeleteWarning(null);
				}
				else {
					setErrorMessage(data.errorMessage);
				}
			}
		});
	}

	function handleGridFilterApplyClick() {
		let qdesk = (window as any).qdesk;

		var aSelectedIds = (window as any).fnGetValuesByColumn("Id", grid);
		if (aSelectedIds.length == 1) {
			props.onSelect(aSelectedIds[0]);
			setOpen(false);
		}
		else {
			qdesk.dialog.show(t("information"), t("selectSingleFilter"));
		}
	}

	return <div className={props.className}>
		<Button onClick={() => setOpen(true)}>
			{t("control.filtering.management")}
		</Button>
		{open && <Dialog
			open={true}
			maxWidth="lg"
			fullWidth
		>
			<DialogTitleWithClose onClose={() => setOpen(false)}>{t("control.filtering.filtersManagement")}</DialogTitleWithClose>
			<DialogContent>
				<div>
					<Button
						variant="outlined"
						onClick={handleGridFilterDeleteClick}
						disabled={deleteDisabled}
						endIcon={<Icon>delete</Icon>}
						style={{marginRight: '14px'}}
					>
						{t("common.action.delete")}
					</Button>
			
					<Button 
						variant="outlined"
						onClick={handleGridFilterApplyClick}
						disabled={applyDisabled}
					>
						{t("control.filtering.applyFilter")}
					</Button>
				</div>
				<FiltersGrid
					typeName={props.typeName}
					onInited={(gr) => setGrid(gr)}
					onRowClick={(selected) => {
						setApplyDisabled(selected != 1);
						setDeleteDisabled(selected == 0);
						setErrorMessage("");
					}}
					onRowDblClick={(aData) => {
						props.onSelect(aData[0]);
						setOpen(false);
					}}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => setOpen(false)}
					variant="contained"
				>
					{t("common.action.close")}
				</Button>
			</DialogActions>
		</Dialog>}
		<Dialog open={deleteWarning !== null}>
			<DialogTitle>{t("common.question")}</DialogTitle>
			<DialogContent>
				{errorMessage && <DialogContentText className="text-danger">{errorMessage}</DialogContentText>}
				<DialogContentText>{t("common.message.areYouSureYouWantToDeleteItems")}</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					onClick={() => setDeleteWarning(null)}
				>
					{t("common.action.cancel")}
				</Button>
				<Button
					onClick={handleAcceptDelete}
					variant="contained"
					color="secondary"
				>
					{t("common.action.ok")}
				</Button>
			</DialogActions>
		</Dialog>
		<InformationDialog
			info={informationDialog}
			onClose={() => setInformationDialog(null)}
		/>
	</div>
}

interface IFiltersGridProps {
	onRowDblClick: (aData) => void,
	onRowClick: (selectedCount: number) => void,
	typeName: string,
	onInited: (grid) => void,
}

function FiltersGrid(props: IFiltersGridProps)
{
	const { t } = useTranslation();
	const tableRef = React.useRef<HTMLTableElement>();
	const gridFilterData = React.useRef<any>();

	React.useEffect(() => {
		initGrid();
		gridFilterData.current?.fnAutoSizeInputs();
	}, []);


	function initGrid() {

		const columns = [
			{ "sName": "Id", "sTitle": t("control.filtering.id"), "sType": "string", "bVisible": false },
			{ "sName": "Name", "sTitle": t("control.filtering.name"), "sType": "string" },
			{ "sName": "IsPrivate", "sTitle": t("control.filtering.isPrivate"), "sType": "boolean" },
			{ "sName": "TypeName", "sTitle": t("control.filtering.type"), "sType": "string", "bSortable": false },
			{ "sName": "Expression", "sTitle": t("control.filtering.expression"), "sType": "string", "bVisible": false },
			{ "sName": "CreatedBy", "sTitle": t("control.filtering.createdBy"), "sType": "string" },
			{ "sName": "Predefined", "sTitle": t("control.filtering.predefined"), "sType": "boolean" }
		];

		let $ = (window as any).$;
		
		gridFilterData.current = new (window as any).GridSetup()
			.forElement($(tableRef.current))
			.withColumnSelector()
			.setDisplayLength(10)
			.relateFilteringWith(props.typeName)
			.setColumns(columns)
			.setAjaxSource("/Filters/GetFiltersByType")
			.onRowClick(() => {
				let selected = (window as any).fnGetSelected(gridFilterData.current).length;
				props.onRowClick(selected);
				
			})
			.onRowDblClick(function (nRow, aData, iDisplayIndex) {
				props.onRowDblClick(aData)
			})
			.Create();

		props.onInited(gridFilterData.current);
	}

	return <div>
		<table className="display" ref={tableRef}>
			<tfoot><tr></tr></tfoot>
		</table>
	</div>
}